import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

const TIER_STYLES = {
  diamond: "from-cyan-400/20 to-purple-500/20 border-cyan-300/40 text-cyan-100",
  platinum: "from-slate-300/20 to-slate-500/20 border-slate-300/40 text-slate-100",
  gold: "from-amber-400/20 to-yellow-600/20 border-amber-400/40 text-amber-100",
  silver: "from-zinc-300/15 to-zinc-500/15 border-zinc-300/30 text-zinc-100",
};

const StatTile = ({ label, value, icon, accent = "purple", testid }) => (
  <div
    className={`rounded-xl px-3 py-3 bg-${accent}-500/10 border border-${accent}-500/30`}
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
      <span>{icon}</span>
      <span>{label}</span>
    </div>
    <div className="text-2xl font-black text-white mt-0.5">
      {(value ?? 0).toLocaleString()}
    </div>
  </div>
);

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
};

const AchievementsTab = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/users/${userId}/achievements`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load achievements");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div className="py-10 text-center text-gray-400" data-testid="achievements-loading">
        Loading trophy case…
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-10 text-center text-rose-300" data-testid="achievements-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const stats = data.stats || {};
  const achievements = data.achievements || [];
  const recentWins = data.recent_spotlight_wins || [];
  const hasAny = achievements.length > 0
    || stats.spotlight_wins_total > 0
    || stats.coins_earned_total > 0;

  return (
    <div className="space-y-6" data-testid="achievements-tab">
      {/* Headline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="achievements-stats-grid">
        <StatTile
          icon="🏆" label="Spotlight wins"
          value={stats.spotlight_wins_total}
          accent="amber"
          testid="stat-spotlight-wins"
        />
        <StatTile
          icon="🪙" label="Coins earned"
          value={stats.coins_earned_total}
          accent="amber"
          testid="stat-coins-earned"
        />
        <StatTile
          icon="▶" label="Promo plays"
          value={stats.promotional_plays_total}
          accent="purple"
          testid="stat-promo-plays"
        />
        <StatTile
          icon="🎯" label="Referrals"
          value={stats.referrals_count}
          accent="emerald"
          testid="stat-referrals"
        />
      </div>

      {!hasAny && (
        <Card className="p-8 text-center bg-white/[0.02] border-white/10" data-testid="achievements-empty">
          <div className="text-4xl mb-2">🎯</div>
          <p className="text-white font-semibold">No trophies yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Win a Spotlight, grow a crew, get verified, or refer artists — every milestone shows up here.
          </p>
        </Card>
      )}

      {/* Trophy cards */}
      {achievements.length > 0 && (
        <div>
          <h3 className="text-white font-bold text-base mb-2 flex items-center gap-2">
            🏅 Achievements
            <span className="text-xs font-normal text-gray-400">
              ({achievements.length})
            </span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="achievements-list">
            {achievements.map((a) => {
              const tier = TIER_STYLES[a.tier] || TIER_STYLES.silver;
              return (
                <div
                  key={a.id}
                  className={`relative rounded-xl p-4 bg-gradient-to-br ${tier} border`}
                  data-testid={`achievement-${a.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-3xl flex-shrink-0">{a.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-white">{a.title}</div>
                      <div className="text-xs text-white/70 mt-0.5">
                        {a.description}
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-bold text-white/40">
                    {a.tier}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Spotlight wins */}
      {recentWins.length > 0 && (
        <div>
          <h3 className="text-white font-bold text-base mb-2 flex items-center gap-2">
            🏆 Spotlight history
            <span className="text-xs font-normal text-gray-400">
              ({recentWins.length})
            </span>
          </h3>
          <ul className="space-y-1.5" data-testid="achievements-spotlight-history">
            {recentWins.map((w) => (
              <li
                key={`${w.spotlight_id}-${w.submission_id}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-white/[0.03] border border-white/5"
                data-testid={`spotlight-win-${w.submission_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl flex-shrink-0">
                    {w.position === 1 ? "🥇" : w.position === 2 ? "🥈" : w.position === 3 ? "🥉" : "•"}
                  </span>
                  <div className="min-w-0">
                    <Link
                      to={`/track/${w.submission_id}`}
                      className="text-white hover:text-purple-300 font-semibold truncate block"
                    >
                      {w.track_title || "Track"}
                    </Link>
                    <div className="text-[11px] text-gray-400">
                      {w.week_label || "Spotlight"} · {formatDate(w.week_start)}
                    </div>
                  </div>
                </div>
                <div className="text-amber-300 text-xs font-bold flex-shrink-0">
                  #{w.position}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coins breakdown by reward type */}
      {data.coins_by_type && Object.keys(data.coins_by_type).length > 0 && (
        <div>
          <h3 className="text-white font-bold text-base mb-2">🪙 Coin rewards breakdown</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="achievements-coins-breakdown">
            {Object.entries(data.coins_by_type).map(([type, info]) => (
              <div
                key={type}
                className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2"
                data-testid={`coins-by-type-${type}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-gray-400">
                  {info.label || type}
                </div>
                <div className="text-amber-300 font-bold">+{(info.total_coins || 0).toLocaleString()}</div>
                <div className="text-[10px] text-gray-500">× {info.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsTab;
